// @givmo/board — "Upload your board’s older files" (CBG-AI5-INBOX-WEB, D-CBG-41; design-284
// `scratchpad/2026-10-24-import-inbox-design/design.md` §1–§3, frames u2–u4).
//
// A person drops a whole folder here. Each file goes up through a person's slot straight to Board's
// own storage, and Board checks it as it lands. The person's own AI, any AI with the Givmo
// connector, then lists the uploads, reads each one through the connector and proposes a past record
// for it, naming the upload by id; the person confirms on the confirmation page as for any proposal.
//
// ══ THE RULES THE PAGE HOLDS ═════════════════════════════════════════════════════════════════════
//
//   1. NOTHING HERE IS THE RECORD. An upload waits for the AI; a record proposed from it waits for
//      the person's confirmation (D-CBG-34). The page's one primary act is "Review them", which opens
//      that confirmation.
//   2. A FOLDER OVER THE LIMITS GOES UP NOT AT ALL (design §3): 1,000 files and 10 GB waiting at once,
//      the backend's own figures, checked before anything uploads. A file Board takes none of (by its
//      type, or over 50 MB) is said at once and never asks for a slot.
//   3. WHAT NEEDS ATTENTION COMES FIRST (frame u3): checking, uploading, then a file that can't be
//      added, then one Board flags ("Already in your minute book", "Already uploaded"), then the
//      rest. A flag is quiet, never a refusal.
//   4. AN UPLOAD IS THE ORGANIZATION'S OWN FILE, never deleted by age or in silence (D-CBG-41 answer
//      3): it waits until a person removes it, and "Remove from your uploads" is the only way it
//      goes. A row Board took nothing for is only cleared ("Clear"), never called a removal.
//   5. A FILE'S NAME IS THE PERSON'S: plain text only, as every name on Board's pages.
//
// Dark until served (`upload-queries.ts`): while the backend's `BOARD_IMPORT_INBOX_ENABLED` is off,
// the uploads' read answers the absent-route 404 and this page hands over to the import page, as a
// batch's page does. Shown only to someone holding `board_record:import`.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { firstErrorCode, isGivmoError, type BoardProposalFileContentType } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Icon,
  Meter,
  RowActions,
  Skeleton,
  cn,
  saasButtonVariants,
  toast,
} from '@givmo/design-system';
import { FileText, FolderUp, Image as PictureIcon } from 'lucide-react';
import { useBoardHost } from '../../board-host';
import { BOARD_PAGES } from '../../board-routes';
import { Link, useNavigate } from '../../board-routing';
import { useBoardOperations } from '../../board-runtime';
import { PageShell } from '../../components/page-shell';
import { displayDate } from '../../lib/display';
import { isNotServed } from '../../lib/not-served';
import { FILE_ACCEPT, chosenFileType } from '../proposals/proposal-decode';
import { isWordFile } from '../proposals/proposal-words';
import { fileSize } from '../returns/return-decode';
import { servedSentence } from '../returns/return-queries';
import {
  fitsTheLimits,
  isEmpty,
  isPicture,
  kindWord,
  limitWords,
  proposedSets,
  type InboxFile,
  type ProposedSet,
} from './upload-decode';
import { importInboxKey, useImportInbox } from './upload-queries';
import {
  putWithProgress,
  filesFromDrop,
  filesFromInput,
  type ChosenFile,
} from './upload-transport';
import { uploadWords as w } from './upload-words';

const LINE = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const HEADING = 'font-sans text-s15 font-w600 tracking-givmo text-text-strong';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const BACK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

/** How many files go up at once. */
const AT_ONCE = 3;

/** The folder input's attribute (cut inbox's review fold, M8), which React's props don't type. */
const FOLDER_INPUT = { webkitdirectory: '' } as Record<string, string>;

/** Where one file of a folder stands on its way up (frame u3). */
type Phase = 'queued' | 'uploading' | 'checking' | 'done' | 'refused' | 'failed';

/** One file of the folders dropped since the page opened. */
type Going = {
  key: string;
  file: File;
  name: string;
  size: number;
  contentType: BoardProposalFileContentType | null;
  phase: Phase;
  percent: number;
  /** Why it can't be added, or why it didn't upload. */
  said: string | null;
  /** What to do about a refusal: its next step (cut inbox's review fold, M5), or none. */
  next: string | null;
  /** Board's answer once its check passed. */
  uploaded: InboxFile | null;
};

const IN_FLIGHT: readonly Phase[] = ['queued', 'uploading', 'checking'];

/** Rule 3's order: checking, uploading (and waiting to), can't be added, flagged, the rest. */
function rank(item: Going): number {
  switch (item.phase) {
    case 'checking':
      return 0;
    case 'uploading':
    case 'queued':
      return 1;
    case 'refused':
    case 'failed':
      return 2;
    default:
      return item.uploaded?.already_recorded_record_id || item.uploaded?.already_uploaded_file_id
        ? 3
        : 4;
  }
}

function byAttention(left: Going, right: Going): number {
  return rank(left) - rank(right) || left.name.localeCompare(right.name);
}

/** One file's quiet line: its kind, size, a PDF's pages and the day it was uploaded. */
function fileLine(file: {
  content_type: string | null;
  byte_size: number;
  page_count?: number | null;
  uploaded_at?: string | null;
}): string {
  return w.fileLine(
    kindWord(file.content_type),
    fileSize(file.byte_size),
    typeof file.page_count === 'number' ? file.page_count : null,
    file.uploaded_at ? displayDate(file.uploaded_at) : null,
  );
}

/** What a failed upload or check says on its row, and the next step a refusal names. */
function refusalOf(caught: unknown): Pick<Going, 'phase' | 'said' | 'next'> {
  if (isGivmoError(caught) && !isNotServed(caught)) {
    switch (firstErrorCode(caught)) {
      case 'wrong_type':
        return { phase: 'refused', said: w.notAPdfOrPicture, next: w.nextStepType };
      case 'too_large':
        return { phase: 'refused', said: w.tooLarge, next: w.nextStepSize };
      case 'unreadable':
        return { phase: 'refused', said: w.unreadable, next: w.nextStepUnreadable };
      case 'import_inbox_full':
        // The served sentence says what to do (remove some, or let the AI propose from them).
        return { phase: 'refused', said: servedSentence(caught, w.didNotUpload), next: null };
      default:
        return { phase: 'failed', said: servedSentence(caught, w.didNotUpload), next: null };
    }
  }
  return { phase: 'failed', said: w.didNotUpload, next: null };
}

export function ImportUploadsPage() {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isReady, orgRef } = useBoardHost();
  const { allowed, read } = useImportInbox();
  const inbox = read.data;
  const [items, setItems] = useState<Going[]>([]);
  const [overLimits, setOverLimits] = useState(false);
  // The finish, said plainly once every file Board takes is up (cut inbox's review fold, M4): it
  // outlives the rows, which then wait with the rest, until more files are added.
  const [finished, setFinished] = useState(false);
  const started = useRef(new Set<string>());
  const nextKey = useRef(0);

  // Not served here (dark), or a reader who can't import: the import page takes over.
  const handOver = isReady && (!allowed || (read.isSuccess && inbox === null));
  useEffect(() => {
    if (handOver) void navigate({ to: BOARD_PAGES.importBatches, replace: true });
  }, [handOver, navigate]);

  const settle = useCallback(
    () => queryClient.invalidateQueries({ queryKey: importInboxKey(orgRef) }),
    [queryClient, orgRef],
  );

  const update = useCallback((key: string, patch: Partial<Going>) => {
    setItems((all) => all.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  }, []);

  const send = useCallback(
    async (item: Going) => {
      if (orgRef === null || item.contentType === null) return;
      const body = { file_name: item.name, content_type: item.contentType };
      try {
        const { data: slot } = await operations.officerIssueBoardImportInboxUploadUrl({
          path: { org_ref: orgRef },
          body,
          throwOnError: true,
        });
        if (item.size > slot.max_byte_size) {
          update(item.key, { phase: 'refused', said: w.tooLarge, next: w.nextStepSize });
          return;
        }
        const uploadId = slot.upload_id ?? '';
        const stored =
          uploadId.length > 0 &&
          (await putWithProgress(slot, item.file, (percent) => update(item.key, { percent })));
        if (!stored) {
          update(item.key, { phase: 'failed', said: w.didNotUpload });
          return;
        }
        update(item.key, { phase: 'checking', percent: 100 });
        const { data: uploaded } = await operations.officerCheckBoardImportInboxFile({
          path: { org_ref: orgRef, upload_id: uploadId },
          body,
          throwOnError: true,
        });
        update(item.key, { phase: 'done', uploaded });
      } catch (caught) {
        update(item.key, refusalOf(caught));
      } finally {
        void settle();
      }
    },
    [operations, orgRef, settle, update],
  );

  // Start what waits, AT_ONCE at a time; each key starts once.
  useEffect(() => {
    const running = items.filter(
      (item) => item.phase === 'uploading' || item.phase === 'checking',
    ).length;
    const next = items
      .filter((item) => item.phase === 'queued' && !started.current.has(item.key))
      .slice(0, Math.max(0, AT_ONCE - running));
    for (const item of next) {
      started.current.add(item.key);
      update(item.key, { phase: 'uploading', percent: 0 });
      void send(item);
    }
  }, [items, send, update]);

  // Every file up and checked, none to see to: they wait with the rest, in their groups, and the
  // page says so.
  const settled =
    items.length > 0 && items.every((item) => item.phase === 'done') && !read.isFetching;
  useEffect(() => {
    if (!settled) return;
    setItems([]);
    setFinished(true);
  }, [settled]);

  const add = useCallback(
    (files: readonly ChosenFile[]) => {
      if (!inbox || files.length === 0) return;
      setFinished(false);
      const chosen = files.map(({ file, name }) => ({
        file,
        name,
        contentType: chosenFileType(file),
      }));
      const taken = chosen.filter(
        (one) => one.contentType !== null && one.file.size <= inbox.maxFileByteSize,
      );
      // The files already on their way count too: the read holds them only once checked.
      const goingSizes = items
        .filter((item) => IN_FLIGHT.includes(item.phase))
        .map((item) => item.size);
      if (!fitsTheLimits(inbox, [...goingSizes, ...taken.map((one) => one.file.size)])) {
        setOverLimits(true);
        return;
      }
      setOverLimits(false);
      const added = chosen.map(({ file, name, contentType }): Going => {
        nextKey.current += 1;
        const [refusal, next] =
          contentType === null
            ? [w.notAPdfOrPicture, w.nextStepType]
            : file.size > inbox.maxFileByteSize
              ? [w.tooLarge, w.nextStepSize]
              : [null, null];
        return {
          key: `going-${nextKey.current}`,
          file,
          name,
          size: file.size,
          contentType,
          phase: refusal === null ? 'queued' : 'refused',
          percent: 0,
          said: refusal,
          next,
          uploaded: null,
        };
      });
      setItems((all) => [...all, ...added]);
    },
    [inbox, items],
  );

  const forget = (key: string) => setItems((all) => all.filter((item) => item.key !== key));
  const retry = (key: string) => {
    started.current.delete(key);
    update(key, { phase: 'queued', percent: 0, said: null, next: null });
  };

  const remove = useMutation({
    mutationFn: async (uploadId: string) => {
      await operations.officerRemoveBoardImportInboxFile({
        path: { org_ref: orgRef ?? '', upload_id: uploadId },
        throwOnError: true,
      });
      return uploadId;
    },
    onSuccess: (uploadId) => {
      setItems((all) => all.filter((item) => item.uploaded?.id !== uploadId));
      void settle();
    },
    onError: (caught) => {
      toast.error(servedSentence(caught, w.couldNotRemove));
      void settle();
    },
  });

  const open = useMutation({
    mutationFn: async (uploadId: string) => {
      const { data } = await operations.officerGetBoardImportInboxFileUrl({
        path: { org_ref: orgRef ?? '', upload_id: uploadId },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    },
    onError: (caught) => toast.error(servedSentence(caught, w.couldNotOpen)),
  });

  const back = (
    <Link to={BOARD_PAGES.importBatches} exact className={BACK} data-testid="uploads-back">
      {w.backToImport}
    </Link>
  );

  if (!isReady || handOver || (allowed && read.isPending)) {
    return (
      <PageShell title={w.title} actions={back} testId="page-import-uploads">
        <Skeleton className="h-90 w-full rounded-lg" testId="uploads-loading" />
      </PageShell>
    );
  }

  const going = [...items].sort(byAttention);
  const goingIds = new Set(
    items.map((item) => item.uploaded?.id).filter((id): id is string => typeof id === 'string'),
  );
  const shown = (files: readonly InboxFile[]) => files.filter((file) => !goingIds.has(file.id));
  // The count says only what is true (cut inbox's review fold, M4; review-309 F3): uploaded is
  // what Board checked in, and a file it took none of, or one that didn't upload, is said apart.
  const uploadedCount = items.filter((item) => item.phase === 'done').length;
  const refusedCount = items.filter((item) => item.phase === 'refused').length;
  const failedCount = items.filter((item) => item.phase === 'failed').length;
  const inFlight = items.filter((item) => IN_FLIGHT.includes(item.phase)).length;
  const goingCount = [
    inFlight > 0
      ? w.uploadedOf(uploadedCount, items.length - refusedCount)
      : w.uploadedCount(uploadedCount),
    refusedCount > 0 ? w.cantBeAddedCount(refusedCount) : null,
    failedCount > 0 ? w.didNotUploadCount(failedCount) : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
  // Nothing left going up or to try again, and something uploaded: the rest are in.
  const restUploaded = inFlight === 0 && failedCount === 0 && uploadedCount > 0;
  const empty = inbox ? isEmpty(inbox) && items.length === 0 : false;
  const fileActions = (file: InboxFile) => [
    {
      label: isWordFile(file.content_type) ? w.downloadTheFile : w.openTheFile,
      testId: `uploads-menu-open-${file.id}`,
      onSelect: () => open.mutate(file.id),
    },
    {
      label: w.remove,
      testId: `uploads-menu-remove-${file.id}`,
      destructive: true,
      onSelect: () => remove.mutate(file.id),
    },
  ];

  return (
    <PageShell title={w.title} description={w.lead} actions={back} testId="page-import-uploads">
      <div className="grid grid-cols-1 items-start gap-24 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-16">
          <DropZone compact={!empty} onFiles={add} />
          {overLimits && inbox ? (
            <ErrorMessage testId="uploads-over-limits">
              {w.overTheLimits(limitWords(inbox).files, limitWords(inbox).bytes)}
            </ErrorMessage>
          ) : null}
          {finished && items.length === 0 ? (
            <p className={HEADING} role="status" data-testid="uploads-all-uploaded">
              {w.allUploaded}
            </p>
          ) : null}

          {read.isError ? (
            <Card testId="uploads-error">
              <CardBody className="flex flex-col items-start gap-12">
                <p className={LINE}>{w.couldNotLoad}</p>
                <Button
                  appearance="saas"
                  tone="secondary"
                  onClick={() => void read.refetch()}
                  testId="uploads-retry"
                >
                  {w.tryAgain}
                </Button>
              </CardBody>
            </Card>
          ) : null}

          {going.length > 0 ? (
            <Card testId="uploads-going">
              <CardBody className="flex flex-col gap-12">
                <h2 className={HEADING} data-testid="uploads-going-count">
                  {goingCount}
                </h2>
                {restUploaded ? (
                  <p className={LINE} role="status" data-testid="uploads-rest-uploaded">
                    {w.restUploaded}
                  </p>
                ) : null}
                <ul className="flex flex-col">
                  {going.map((item) => (
                    <GoingRow
                      key={item.key}
                      item={item}
                      onForget={() => forget(item.key)}
                      onRetry={() => retry(item.key)}
                      actions={item.uploaded ? fileActions(item.uploaded) : []}
                    />
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {inbox
            ? proposedSets(shown(inbox.proposed)).map((set) => (
                <ProposedCard key={set.batchId} set={set} />
              ))
            : null}

          {inbox && shown(inbox.waiting).length > 0 ? (
            <Card testId="uploads-waiting">
              <CardBody className="flex flex-col gap-12">
                <div className="flex flex-col gap-4">
                  <h2 className={HEADING}>{w.waitingHeading(inbox.waitingCount)}</h2>
                  <p className={QUIET} data-testid="uploads-waiting-lead">
                    {w.waitingLead}
                  </p>
                </div>
                <ul className="flex flex-col">
                  {shown(inbox.waiting).map((file) => (
                    // A quiet flag beside the menu says something, so at a phone's width it moves
                    // under the name, as a going row's does (CBG-FIX169).
                    <FileRow
                      key={file.id}
                      file={file}
                      testId={`uploads-file-${file.id}`}
                      wide={
                        Boolean(file.already_recorded_record_id) ||
                        Boolean(file.already_uploaded_file_id)
                      }
                    >
                      <Flags file={file} />
                      <RowActions
                        testId={`uploads-actions-${file.id}`}
                        label={`Actions for ${file.file_name}`}
                        actions={fileActions(file)}
                      />
                    </FileRow>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {inbox && inbox.imported.length > 0 ? (
            <Card testId="uploads-in-the-book">
              <CardBody className="flex flex-col gap-12">
                <h2 className={HEADING}>{w.inTheBookHeading(inbox.importedCount)}</h2>
                {inbox.importedCount > inbox.imported.length ? (
                  <p className={QUIET}>
                    {w.inTheBookMore(inbox.imported.length, inbox.importedCount)}
                  </p>
                ) : null}
                <ul className="flex flex-col">
                  {inbox.imported.map((file) => (
                    <FileRow key={file.id} file={file} testId={`uploads-file-${file.id}`}>
                      {file.record_id ? (
                        <Link
                          to="/records/$recordId"
                          params={{ recordId: file.record_id }}
                          className={LINK}
                          data-testid={`uploads-open-record-${file.id}`}
                        >
                          {w.openTheRecord}
                        </Link>
                      ) : null}
                    </FileRow>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}
        </div>

        <ThenAsk />
      </div>
    </PageShell>
  );
}

/** The drop zone: the page's whole first view when nothing is here (u2), one line after (u3, u4). */
function DropZone({
  compact,
  onFiles,
}: {
  compact: boolean;
  onFiles: (files: readonly ChosenFile[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setOver(false);
    void filesFromDrop(event.dataTransfer).then(onFiles);
  };
  const onChosen = (event: ChangeEvent<HTMLInputElement>) => {
    onFiles(filesFromInput(event.target.files));
    // The same files chosen again still fire a change.
    event.target.value = '';
  };
  const size = compact ? 'sm' : 'md';
  const choose = (
    <span className="flex flex-wrap items-center justify-center gap-8">
      <Button
        appearance="saas"
        tone="secondary"
        size={size}
        onClick={() => folderInput.current?.click()}
        testId="uploads-choose-folder"
      >
        {w.chooseFolder}
      </Button>
      <Button
        appearance="saas"
        tone="secondary"
        size={size}
        onClick={() => input.current?.click()}
        testId="uploads-choose"
      >
        {w.chooseFiles}
      </Button>
    </span>
  );

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={cn(
        'rounded-lg border-2 border-dashed transition-colors',
        over ? 'border-brand-500 bg-brand-50' : 'border-border-strong bg-surface',
        compact
          ? 'flex items-center justify-between gap-12 px-16 py-10'
          : 'flex flex-col items-center gap-12 px-24 py-32 text-center',
      )}
      data-testid="uploads-drop-zone"
      data-compact={compact}
    >
      <input
        ref={input}
        type="file"
        multiple
        accept={FILE_ACCEPT}
        className="hidden"
        onChange={onChosen}
        data-testid="uploads-input"
      />
      {/* A folder's own way in (M8): every file in it and in the folders inside it. The attribute
          is the browsers' common one, outside React's typed props. */}
      <input
        ref={folderInput}
        type="file"
        multiple
        className="hidden"
        onChange={onChosen}
        data-testid="uploads-folder-input"
        {...FOLDER_INPUT}
      />
      {compact ? (
        <>
          <span className="flex min-w-0 items-center gap-10">
            <Icon icon={FolderUp} size={18} aria-hidden className="shrink-0 text-brand-600" />
            <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
              {w.dropMore}
            </span>
          </span>
          {choose}
        </>
      ) : (
        <>
          <span className="flex size-44 items-center justify-center rounded-full bg-brand-50">
            <Icon icon={FolderUp} size={20} aria-hidden className="text-brand-600" />
          </span>
          <span className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            {w.dropHere}
          </span>
          <span className={QUIET}>{w.or}</span>
          {choose}
          <span className={`${QUIET} max-w-[420px]`}>{w.whatItTakes}</span>
          <span className={`${QUIET} max-w-[420px]`} data-testid="uploads-folders-inside">
            {w.foldersInside}
          </span>
        </>
      )}
    </div>
  );
}

/** "Then ask your AI assistant": the sentence to copy, beside the drop zone (u2–u4). */
function ThenAsk() {
  const { connectedAi } = useBoardHost();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(w.askSentence);
      setCopied(true);
    } catch {
      // Clipboard access can be refused; the sentence is on screen and selectable.
      toast.error(w.couldNotCopy);
    }
  };
  return (
    <Card testId="uploads-then-ask">
      <CardBody className="flex flex-col gap-12">
        <h2 className={HEADING}>{w.thenAsk}</h2>
        <p className={LINE} data-testid="uploads-ask-when">
          {w.askWhen}
        </p>
        <div className="flex items-start justify-between gap-12 rounded-md border border-border-subtle bg-surface-sunken p-12">
          <span
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-strong"
            data-testid="uploads-ask-sentence"
          >
            {w.askSentence}
          </span>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void copy()}
            testId="uploads-copy"
          >
            {copied ? w.copied : w.copy}
          </Button>
        </div>
        <p className={QUIET} data-testid="uploads-what-the-ai-reads">
          {w.whatTheAiReads}
        </p>
        {connectedAi ? (
          <a href={connectedAi.href} className={LINK} data-testid="uploads-see-whats-connected">
            {w.seeWhatsConnected}
          </a>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** One file's row: its icon, name and quiet line, and what goes on its right.
 *
 *  `wide`: what goes on the right carries words (a refusal and its next step, a failure, the quiet
 *  flags). At a phone's width there is no room for them beside the name, so they move onto their
 *  own line under it, lined up with the name, and the row wraps inside the page as it does on a
 *  wide screen (CBG-FIX169). Wider screens keep the one line. */
function FileRow({
  file,
  testId,
  wide = false,
  children,
}: {
  file: { file_name: string; content_type: string | null } & Parameters<typeof fileLine>[0];
  testId: string;
  wide?: boolean;
  children?: ReactNode;
}) {
  return (
    <li
      className={cn(
        'flex items-center gap-12 border-t border-border-subtle py-10 first:border-t-0 first:pt-0 last:pb-0',
        wide && 'max-sm:flex-wrap max-sm:gap-y-6',
      )}
      data-testid={testId}
    >
      <Icon
        icon={isPicture(file.content_type) ? PictureIcon : FileText}
        size={18}
        aria-hidden
        className="shrink-0 text-icon-muted"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="truncate font-sans text-s14 font-w500 tracking-givmo text-text-strong">
          {file.file_name}
        </span>
        <span className={QUIET}>{fileLine(file)}</span>
      </span>
      <span
        className={cn(
          'flex shrink-0 items-center gap-12',
          // Under the name: the icon's 18px and the row's 12px gap.
          wide &&
            'max-sm:min-w-0 max-sm:shrink max-sm:basis-full max-sm:justify-between max-sm:pl-30',
        )}
      >
        {children}
      </span>
    </li>
  );
}

/** The quiet flags (frame u3): already in the minute book (linked), or already uploaded. */
function Flags({ file }: { file: InboxFile }) {
  if (file.already_recorded_record_id) {
    return (
      <span className={LINE} data-testid={`uploads-in-the-book-${file.id}`}>
        <span className="text-s13">{w.alreadyInTheBook} · </span>
        <Link
          to="/records/$recordId"
          params={{ recordId: file.already_recorded_record_id }}
          className={LINK}
          data-testid={`uploads-in-the-book-link-${file.id}`}
        >
          {w.openTheRecord}
        </Link>
      </span>
    );
  }
  if (file.already_uploaded_file_id) {
    return (
      <span className={QUIET} data-testid={`uploads-already-uploaded-${file.id}`}>
        {w.alreadyUploaded}
      </span>
    );
  }
  return null;
}

/** One file of a folder on its way up (frame u3). */
function GoingRow({
  item,
  onForget,
  onRetry,
  actions,
}: {
  item: Going;
  onForget: () => void;
  onRetry: () => void;
  actions: { label: string; testId: string; destructive?: boolean; onSelect: () => void }[];
}) {
  const shown = item.uploaded ?? {
    file_name: item.name,
    content_type: item.contentType,
    byte_size: item.size,
    page_count: null,
    // No date until Board has the file: the line's date is the upload's (M3), and a file still
    // going up, or one Board takes none of, hasn't been uploaded.
    uploaded_at: null,
  };
  const testId = `uploads-going-${item.key}`;
  // A row whose right side says something (a refusal, a failure, a quiet flag), never a lone
  // control, moves it under the name at a phone's width (CBG-FIX169).
  const flagged =
    item.phase === 'done' &&
    item.uploaded !== null &&
    (Boolean(item.uploaded.already_recorded_record_id) ||
      Boolean(item.uploaded.already_uploaded_file_id));
  return (
    <FileRow
      file={shown}
      testId={testId}
      wide={item.phase === 'refused' || item.phase === 'failed' || flagged}
    >
      {item.phase === 'queued' ? (
        <span className={QUIET} data-testid={`${testId}-queued`}>
          {w.queued}
        </span>
      ) : null}
      {item.phase === 'uploading' ? (
        <Meter
          value={item.percent}
          size="sm"
          label={w.uploading(item.percent)}
          className="w-[180px]"
          testId={`${testId}-progress`}
        />
      ) : null}
      {item.phase === 'checking' ? (
        <span className={QUIET} data-testid={`${testId}-checking`}>
          {w.checking}
        </span>
      ) : null}
      {item.phase === 'refused' || item.phase === 'failed' ? (
        <>
          <span className="flex max-w-[360px] flex-col items-end gap-2 text-right max-sm:min-w-0 max-sm:flex-1 max-sm:items-start max-sm:text-left">
            <span
              className="font-sans text-s13 font-w500 tracking-givmo text-warning-700"
              data-testid={`${testId}-said`}
            >
              {item.said}
            </span>
            {item.next !== null ? (
              <span className={QUIET} data-testid={`${testId}-next`}>
                {item.next}
              </span>
            ) : null}
          </span>
          {item.phase === 'failed' ? (
            <button
              type="button"
              className={LINK}
              onClick={onRetry}
              data-testid={`${testId}-retry`}
            >
              {w.tryAgain}
            </button>
          ) : null}
          {/* Only the row goes: Board holds nothing of a file it took none of, or that didn't
              upload (M6: the waiting file's menu "Remove from your uploads" is the other act). */}
          <button type="button" className={LINK} onClick={onForget} data-testid={`${testId}-clear`}>
            {w.clear}
          </button>
        </>
      ) : null}
      {item.phase === 'done' && item.uploaded ? (
        <>
          <Flags file={item.uploaded} />
          <RowActions
            testId={`${testId}-actions`}
            label={`Actions for ${item.name}`}
            actions={actions}
          />
        </>
      ) : null}
    </FileRow>
  );
}

/** "Proposed, waiting for you" (frame u4): one card per confirmation, with its one primary act. */
function ProposedCard({ set }: { set: ProposedSet }) {
  return (
    <Card testId={`uploads-proposed-${set.batchId}`}>
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-12 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-4">
            <h2 className={HEADING}>{w.proposedHeading(set.files.length)}</h2>
            <p className={LINE}>{w.proposedLead(set.files.length)}</p>
          </div>
          <Link
            to={BOARD_PAGES.proposedBatch}
            params={{ batchId: set.batchId }}
            className={saasButtonVariants({
              size: 'md',
              tone: 'primary',
              className: 'shrink-0 no-underline',
            })}
            data-testid={`uploads-review-${set.batchId}`}
          >
            {w.reviewThem}
          </Link>
        </div>
        <ul className="flex flex-col">
          {set.files.map((file) => (
            <FileRow key={file.id} file={file} testId={`uploads-file-${file.id}`} />
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
